import type { BrandedAs } from "@@/utilities/branded";
import { customAlphabet, nanoid } from "nanoid/non-secure";
import { z } from "zod";
import { emptyString } from "./empty";
import { randomInt } from "./random-int";
import { setWithinRange } from "./set-within-range";

const defaultOptions = {
    length: 20,
    prefix: "" as `${string}__` | "",
};

// // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
const generateId = <T extends string | BrandedAs<string> = string>(
    options: Partial<typeof defaultOptions> = defaultOptions,
): T => {
    const opt: Required<typeof defaultOptions> = {
        ...defaultOptions,
        ...options,
    };

    return `${opt.prefix.trim().replace(/[^a-z0-9_-]/gim, emptyString)}${nanoid(
        opt.length,
    )}` as T;
};

const substantiv = [
    "allierad",
    "alp",
    "alpha",
    "alptopp",
    "antilop",
    "apa",
    "apel",
    "apelsin",
    "ballong",
    "banan",
    "barnvagn",
    "barrista",
    "basfiol",
    "bastkjol",
    "beta",
    "biceps",
    "bil",
    "björk",
    "björn",
    "blomkruka",
    "blomma",
    "blomsteräng",
    "blåbärskaka",
    "boll",
    "bomullsplanta",
    "bordskniv",
    "borste",
    "bostad",
    "brandman",
    "brevbärare",
    "brevlåda",
    "brygga",
    "bungalow",
    "bunker",
    "burk",
    "buss",
    "busschaufför",
    "byggnad",
    "bytta",
    "båt",
    "båtmotor",
    "cello",
    "cirkel",
    "citron",
    "citrus",
    "cykel",
    "cylinder",
    "dag",
    "dans",
    "dartpil",
    "doja",
    "ek",
    "elefant",
    "elfiol",
    "enhjuling",
    "fanfar",
    "fantasi",
    "fest",
    "figur",
    "fiol",
    "fiskmås",
    "fjällräv",
    "fjälluggla",
    "flaskpost",
    "flaxning",
    "flodhäst",
    "flotte",
    "flöjt",
    "folkhem",
    "fotboll",
    "frihet",
    "fågel",
    "fåtölj",
    "färja",
    "förskola",
    "gaffel",
    "gasell",
    "gatsten",
    "gepard",
    "get",
    "giraff",
    "glasburk",
    "glasstav",
    "gnu",
    "gran",
    "grej",
    "gren",
    "grop",
    "grupp",
    "gräns",
    "gräsmatta",
    "gås",
    "gädda",
    "hammare",
    "hamster",
    "helg",
    "himmel",
    "hund",
    "hustomte",
    "häst",
    "höjd",
    "höna",
    "höst",
    "idyll",
    "igelkott",
    "infart",
    "isglass",
    "jordgubbe",
    "jul",
    "järnväg",
    "kaffekopp",
    "kalabalik",
    "kanin",
    "kapsyl",
    "kassett",
    "kastrull",
    "katedral",
    "katt",
    "kattunge",
    "kavel",
    "kejsare",
    "kikare",
    "kikärta",
    "kiwi",
    "klass",
    "knapp",
    "knäck",
    "ko",
    "koala",
    "kompis",
    "kram",
    "krans",
    "kravatt",
    "kruka",
    "krumelur",
    "krydda",
    "kudde",
    "kung",
    "kvadrant",
    "kvadrat",
    "kvist",
    "kväll",
    "kyckling",
    "kyrka",
    "käresta",
    "kö",
    "ladugård",
    "lampa",
    "last",
    "lejonhona",
    "lek",
    "leksak",
    "leopard",
    "lovikavante",
    "lur",
    "låda",
    "läkare",
    "lämmel",
    "mandrill",
    "mango",
    "midsommar",
    "mjölk",
    "morgon",
    "mungiga",
    "munk",
    "musik",
    "musiker",
    "musköt",
    "måne",
    "möbel",
    "natt",
    "omega",
    "orangutang",
    "palett",
    "pall",
    "palm",
    "pelikan",
    "pianopall",
    "pingisrunda",
    "plantage",
    "plogbil",
    "pod",
    "polis",
    "politiker",
    "pollett",
    "postiljon",
    "postlåda",
    "påsk",
    "radio",
    "rektangel",
    "ren",
    "resa",
    "ringklocka",
    "romb",
    "ros",
    "rymd",
    "räv",
    "sallad",
    "sandal",
    "sandstrand",
    "segelbåt",
    "sengångare",
    "sjö",
    "sked",
    "sko",
    "skola",
    "skridsko",
    "skyffel",
    "skärgård",
    "sladd",
    "slev",
    "slottsruin",
    "snigel",
    "snuttefilt",
    "plåtskiva",
    "ugnsplåt",
    "snö",
    "snödriva",
    "snöflinga",
    "snögubbe",
    "sockerbeta",
    "soffa",
    "sol",
    "sommar",
    "sommarstuga",
    "spade",
    "spatel",
    "sport",
    "staty",
    "sten",
    "stjärna",
    "stol",
    "stuga",
    "stuga",
    "stämpel",
    "svala",
    "svans",
    "syjunta",
    "såg",
    "säng",
    "tamburin",
    "tant",
    "telefon",
    "tiddelipom",
    "tomat",
    "triangel",
    "trombon",
    "trumma",
    "trummis",
    "trädgräns",
    "tupp",
    "tygbit",
    "kolibri",
    "koltrast",
    "komet",
    "kotte",
    "krokodil",
    "tågresa",
    "tågvagn",
    "ukelele",
    "havsörn",
    "undulat",
    "utfart",
    "utopi",
    "vagn",
    "val",
    "valp",
    "jojo",
    "vante",
    "vhs",
    "vind",
    "hed",
    "lycka",
    "villa",
    "vinter",
    "vintergata",
    "vinterstuga",
    "viol",
    "virvel",
    "visp",
    "våg",
    "vår",
    "vårdagjämning",
    "vårmorgon",
    "vårsol",
    "sommarsol",
    "detektiv",
    "vandrare",
    "quinoa",
    "vän",
    "värme",
    "växt",
    "yrsel",
    "zebra",
    "åker",
    "älg",
    "äng",
];
const adjektiv = [
    "alarmerande",
    "allmän",
    "alternerande",
    "altruistisk",
    "aplik",
    "arbetssam",
    "aspirerande",
    "avig",
    "balanserad",
    "blomstrande",
    "blå",
    "brödlik",
    "brölande",
    "busig",
    "charmerande",
    "charmig",
    "cool",
    "daglig",
    "dansande",
    "djup",
    "driftig",
    "elastisk",
    "elektrisk",
    "engelsk",
    "fager",
    "fantasifull",
    "fantastisk",
    "fiffig",
    "fin",
    "flummig",
    "flygande",
    "frusen",
    "fräknig",
    "futtig",
    "följsam",
    "förtjusande",
    "galen",
    "gammal",
    "gigantisk",
    "glad",
    "gnostisk",
    "gnuggande",
    "gosig",
    "grund",
    "gryta",
    "grön",
    "grönskande",
    "gul",
    "gullig",
    "gående",
    "gäll",
    "hastig",
    "hattig",
    "hjälpsam",
    "hoppande",
    "hoppfull",
    "hoppig",
    "humoristisk",
    "hårfin",
    "häftig",
    "hängiven",
    "högljudd",
    "höstig",
    "idyllisk",
    "inbjudande",
    "inspirerande",
    "isig",
    "jamande",
    "kalasig",
    "kastande",
    "kavat",
    "kittel",
    "klantig",
    "klapprig",
    "klok",
    "kluckande",
    "klädsam",
    "knaper",
    "knapp",
    "knaprig",
    "knasig",
    "knastrig",
    "knäsvag",
    "kollektiv",
    "konstig",
    "kramgo",
    "kramig",
    "krispig",
    "krypande",
    "kvadratisk",
    "kvarnlik",
    "kvick",
    "lagom",
    "lantlig",
    "ledig",
    "leende",
    "lekande",
    "lekfull",
    "len",
    "leviterande",
    "likadan",
    "listig",
    "liten",
    "livgivande",
    "ljus",
    "ljuv",
    "lokomotivlik",
    "lovande",
    "lukrativ",
    "lurig",
    "lustig",
    "lutande",
    "lycklig",
    "lång",
    "långsam",
    "läskande",
    "lövig",
    "mastig",
    "maxad",
    "mellanstor",
    "metallisk",
    "mjuk",
    "mustig",
    "mysig",
    "mångsidig",
    "mäktig",
    "mästerlig",
    "neutral",
    "normal",
    "norsk",
    "omtyckt",
    "omtänksam",
    "onormal",
    "ovärderlig",
    "ovärderlig",
    "perfekt",
    "pigg",
    "plan",
    "poppad",
    "pratsam",
    "pyttemjuk",
    "påhittig",
    "pålitlig",
    "påtaglig",
    "rask",
    "rektangulär",
    "ren",
    "riktig",
    "rolig",
    "rosa",
    "rosig",
    "rullande",
    "rund",
    "ryckande",
    "rykande",
    "röd",
    "seg",
    "skrattande",
    "skrattfull",
    "skrikig",
    "skuttande",
    "smakrik",
    "smickrande",
    "smidig",
    "smygande",
    "snabb",
    "snickrande",
    "snygg",
    "snäll",
    "somrig",
    "spansk",
    "spelad",
    "spelande",
    "spetsig",
    "spirande",
    "springande",
    "stark",
    "stjärnfull",
    "stor",
    "strandad",
    "sträv",
    "strävande",
    "studsande",
    "susande",
    "svensk",
    "svetsande",
    "svävande",
    "sömnig",
    "tippande",
    "tokig",
    "trasig",
    "tröstande",
    "turkos",
    "tvättad",
    "typisk",
    "tysk",
    "tyst",
    "tålig",
    "tänkande",
    "törstig",
    "underbar",
    "ung",
    "utomjordisk",
    "utsmyckad",
    "vacker",
    "vaggande",
    "vallad",
    "vilsen",
    "vintrig",
    "viskande",
    "vispande",
    "vit",
    "vyschande",
    "vårig",
    "vän",
    "vänlig",
    "värdefull",
    "yr",
    "älskad",
    "älskvärd",
    "ödmjuk",
    "önskande",
    "önskad",
    "överraskande",
    "överraskad",
];

// Todo: unhardcode banned list
const banned = [
    "blomstrande-syster",
    "gul-vän",
    "röd-vän",
    "trasig-vän",
    "poppad-polis",
    "vit-folkhem",
];

const generateWordSlug = (): string => {
    const first = randomInt({ min: 0, max: adjektiv.length - 1 });
    const second = randomInt({ min: 0, max: substantiv.length - 1 });

    const result = `${adjektiv[first].toLocaleUpperCase()}-${substantiv[
        second
    ].toLocaleUpperCase()}`;

    return banned.includes(result) ? generateWordSlug() : result;
};

const slugAlphabet = "2346789ABCDEFGHJKLMNPQRTUVWXYZ";
const shortIdEight = customAlphabet(slugAlphabet, 8);
const shortIdSix = customAlphabet(slugAlphabet, 6);
const shortIdFour = customAlphabet(slugAlphabet, 4);
const shortCode = (options?: {
    charsToIgnore?: string | string[];
    noNumbers?: boolean;
    /**
     * Defaults to 6 \
     * Must be at least 4
     * @type {number}
     */
    numberOfChars?: number;
}) => {
    const alphabet = (
        options?.noNumbers
            ? "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
            : "2346789ABCDEFGHJKLMNPQRTUVWXYZ"
    )
        .split("")
        .filter(char => !options?.charsToIgnore?.includes(char))
        .join("");
    const numberOfChars = setWithinRange(options?.numberOfChars ?? 6, {
        min: 4,
    });
    return customAlphabet(alphabet)(numberOfChars);
};
/**
 * Creates a short uppercased alphanumeric id
 * @param {(4 | 6 | 8)} [length=6] defaults to 6
 * @return {*}  {string}
 */
const shortId = (length: 4 | 6 | 8 = 6): string => {
    const _length = setWithinRange(length || 6, { min: 4, max: 8 });
    const short =
        _length === 4 ? shortIdFour : _length === 6 ? shortIdSix : shortIdEight;

    return short();
};

const shortIdZodSchemaFactory = <Length extends 4 | 6 | 8>(length: Length) =>
    z
        .string()
        .min(length)
        .max(length)
        .refine(val =>
            val.split("").every(char => slugAlphabet.includes(char)),
        );

const consoleLogMultipleIds = (
    input: number,
    logger: (...logs: unknown[]) => void,
) => {
    for (let index = 0; index < input; index++) {
        logger(generateId({ prefix: "log__" }));
    }
};
export {
    consoleLogMultipleIds,
    generateId,
    generateWordSlug,
    shortCode,
    shortId,
    shortIdZodSchemaFactory,
};
